import { fetchStructuralPage } from './structuralPageQuery';
import { queryPolicies } from './queryPolicies';
import { supabaseCoreClient } from './supabase';

export type GridFilterOptionValue = string | number | boolean | null;

export interface GridFilterOption {
  value: GridFilterOptionValue;
  label: string;
}

export interface GridFilterOptionPage {
  options: GridFilterOption[];
  hasMore: boolean;
}

export interface GridFilterOptionRequest {
  sourceSchema: string;
  sourceName: string;
  columnName: string;
  labelColumnName?: string | null;
  reportKey?: string | null;
  paramValues?: Record<string, unknown>;
  search?: string;
  limit?: number;
  offset?: number;
}

export const GRID_FILTER_OPTIONS_QUERY_KEY = 'grid-filter-options';
export const GRID_FILTER_OPTION_PAGE_SIZE = 100;

export function tableGridSourceName(
  tableName: string,
  source?: 'view' | 'deepView' | 'base',
): string {
  if (source === 'base') return tableName;
  if (source === 'deepView') return `${tableName}_deep_view`;
  return `${tableName}_view`;
}

export async function loadGridFilterOptions(
  request: GridFilterOptionRequest,
): Promise<GridFilterOptionPage> {
  const limit = request.limit ?? GRID_FILTER_OPTION_PAGE_SIZE;
  const offset = request.offset ?? 0;
  const normalizedRequest = {
    ...request,
    search: request.search?.trim() || null,
    limit,
    offset,
  };

  return fetchStructuralPage({
    queryKey: [GRID_FILTER_OPTIONS_QUERY_KEY, normalizedRequest],
    staleTime: queryPolicies.pagedData.staleTime,
    gcTime: queryPolicies.pagedData.gcTime,
    queryFn: async () => {
      const { data, error } = await (supabaseCoreClient as any).rpc('grid_filter_options', {
        p_source_schema: request.sourceSchema,
        p_source_name: request.sourceName,
        p_column_name: request.columnName,
        p_label_column_name: request.labelColumnName ?? null,
        p_report_key: request.reportKey ?? null,
        p_param_values: request.paramValues ?? {},
        p_search: normalizedRequest.search,
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      const rows = (data ?? []) as Array<{ value: GridFilterOptionValue; label: string; has_more: boolean }>;
      return {
        options: rows.map((row) => ({ value: row.value, label: row.label })),
        hasMore: rows.some((row) => row.has_more),
      };
    },
  });
}

export function staticGridFilterOptions(
  options: GridFilterOption[],
  search = '',
  limit = GRID_FILTER_OPTION_PAGE_SIZE,
  offset = 0,
): GridFilterOptionPage {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const matching = normalizedSearch
    ? options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedSearch))
    : options;
  return {
    options: matching.slice(offset, offset + limit),
    hasMore: matching.length > offset + limit,
  };
}
